import type { SectionDraftStatus } from '../api/client'
import type { PublishTarget } from './api'

export type StageKey = 'check_sources' | 'to_review' | 'approved' | 'scheduled' | 'live' | 'taken_down' | 'set_aside'
export type Destination = 'section' | 'membership' | 'both'

const STAGE_OF: Partial<Record<SectionDraftStatus, StageKey>> = {
  sources_ready: 'check_sources',
  draft: 'to_review',
  needs_attention: 'to_review',
  approved: 'approved',
  queued: 'scheduled',
  published: 'live',
  unpublished: 'taken_down',
  rejected: 'set_aside',
  failed: 'set_aside',
}

const STAGE_LABEL: Partial<Record<SectionDraftStatus, string>> = {
  sources_ready: 'Check sources',
  draft: 'Ready for review',
  needs_attention: 'Needs a look',
  approved: 'Approved',
  queued: 'Scheduled',
  published: 'Live',
  unpublished: 'Taken down',
  rejected: 'Set aside',
  failed: "Didn't finish",
}

/** The writer → editor handoff, every label in one place: renaming the button is a one-line change.
 *  Deliberately not "Ready for review", which is the stage label for a written draft (STAGE_LABEL). */
export const HANDOFF = {
  /** The writer's button on a piece. */
  send: 'Send to editor',
  /** The writer withdraws it. */
  takeBack: 'Take back',
  /** The chip on a piece that is with an editor. */
  sentBy: (name: string | null | undefined): string => (name ? `Sent to editor by ${name}` : 'Sent to editor'),
  /** The editor's button: hand it back to the writer. */
  sendBack: 'Send back to writer',
  /** The editors' Desk card. */
  deskEditors: 'Sent by writers',
  /** The writer's own Desk card. */
  deskWriter: 'Waiting for an editor',
  /** The Pieces list filter and the row chip. */
  filter: 'Sent to editor',
} as const

/** Approval: an editor signs a piece off before it can be published or scheduled. */
export const APPROVAL = {
  approve: 'Approve',
  undo: 'Back to review',
  chip: 'Approved',
  /** What a writer sees where an editor sees the Approve button. */
  waiting: 'Waiting for an editor to approve',
  /** The Archive article panel. */
  waitingArticle: 'Waiting for an editor to approve this article',
  /** Asked when approving with open items on the checklist. */
  confirmOpen: (n: number): string =>
    `${n} ${n === 1 ? 'item is' : 'items are'} still open on this piece's checklist. Approve it anyway?`,
} as const

/** What a writer reads when the server refuses an editor's action (editorial_scope.WRITERS_CANT). */
export const WRITERS_CANT = "Writers can't do this — ask an editor of this title."

export function stageOf(status: string): StageKey | null {
  return STAGE_OF[status as SectionDraftStatus] ?? null
}

export function stageLabel(status: string): string {
  return STAGE_LABEL[status as SectionDraftStatus] ?? status
}

export const STAGE_TABS: { key: StageKey | 'all'; label: string }[] = [
  { key: 'to_review', label: 'To review' },
  { key: 'check_sources', label: 'Check sources' },
  { key: 'approved', label: 'Approved' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'live', label: 'Live' },
  { key: 'taken_down', label: 'Taken down' },
  { key: 'set_aside', label: 'Set aside' },
  { key: 'all', label: 'All' },
]

export function destinationLabel(dest: string | null | undefined, sectionLabel: string): string {
  if (dest === 'membership') return 'Members only'
  if (dest === 'both') return `Members + ${sectionLabel}`
  return sectionLabel
}

const CHIP = 'inline-flex h-6 items-center rounded-full px-2.5 text-[12px] font-semibold whitespace-nowrap font-ui'

export function chipClass(status: string): string {
  switch (stageOf(status)) {
    case 'live': return `${CHIP} bg-radar-green/15 text-radar-green`
    case 'approved': return `${CHIP} bg-radar-green/15 text-radar-green`
    case 'scheduled': return `${CHIP} bg-radar-cyan/15 text-radar-cyan`
    case 'check_sources': return `${CHIP} bg-radar-violet/15 text-radar-violet`
    case 'to_review': return status === 'needs_attention'
      ? `${CHIP} bg-amber-glow/20 text-amber-deep`
      : `${CHIP} bg-amber-wash text-amber-deep`
    case 'set_aside': return status === 'failed'
      ? `${CHIP} bg-radar-red/15 text-radar-red`
      : `${CHIP} bg-ink-600 text-paper-dim`
    default: return `${CHIP} bg-ink-600 text-paper-dim`
  }
}

export function destinationChipClass(dest: string | null | undefined): string {
  if (dest === 'membership') return `${CHIP} bg-radar-violet/15 text-radar-violet`
  if (dest === 'both') return `${CHIP} bg-radar-cyan/15 text-radar-cyan`
  return `${CHIP} bg-ink-600 text-paper-dim`
}

const TZ = 'America/New_York'

/** "Oct 6" */
export function etDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' })
}

/** "7:30 AM ET" */
export function etTime(iso: string): string {
  const t = new Date(iso).toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })
  return `${t.replace(/\s/g, ' ')} ET`
}

/** "Tue Oct 6" */
export function etDayShort(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '')
}

/** Where a title's new pieces publish (Settings). */
export function publishTargetLabel(t: PublishTarget): string {
  return t === 'native' ? 'New site' : 'WordPress'
}
